import { FC } from 'react';

import { BUTTON_TYPES, CURRENCIES } from '@savgroup-front-common/constants';
import { Bold } from '@savgroup-front-common/core/atoms/Bold';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { ClickableCard } from '@savgroup-front-common/core/atoms/Cards/ClickableCard/ClickableCard';
import { FormGroup } from '@savgroup-front-common/core/atoms/Form';
import {
  safeFormattedIntlString,
  SafeFormattedMessageWithoutSpread,
} from '@savgroup-front-common/core/formatters';
import { formatStripeAmount } from '@savgroup-front-common/core/formatters/intl';
import { SafeFormattedMessageWithId } from '@savgroup-front-common/core/formatters/SafeFormattedMessageWithId';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { FullScreenLoader } from '@savgroup-front-common/core/molecules/FullScreenLoader';
import { ExternalPortal } from '@savgroup-front-common/core/protons/ExternalPortal/ExternalPortal';
import { ClosedLockUnfilled } from '@savgroup-front-common/core/protons/icons';
import { Kind } from '@/api/Payment/getPaymentSessionQuery';

import { $PaymentButtonContainer } from '../PaymentStripe/CheckoutForm/CheckoutForm.styles';

import { getPaymentMethodDescriptionMessage } from './helpers/getPaymentMethodDescriptionMessage';
import { useGetPurseRedirectionData } from './hooks/useGetPurseRedirectionData';
import messages from './messages';
import { PaymentMethodImage } from './PaymentMethodImage/PaymentMethodImage';
import { usePaymentPurse } from './PaymentPurse.hooks';
import { $SelectPayment } from './PaymentPurse.styles';

interface PaymentPurseProps {
  invoice: { id: string; priceWithTax: number; currencyTrigram: CURRENCIES };
  onSubmitSuccess: () => Promise<void>;
  claimGroupId: string;
  kindId: string;
  kind: Kind;
  isFormCompleted: boolean;
}

export const PaymentPurse: FC<PaymentPurseProps> = ({
  invoice,
  onSubmitSuccess,
  claimGroupId,
  kindId,
  kind,
  isFormCompleted,
}) => {
  const {
    paymentMethods,
    selectedPrimaryToken,
    selectPaymentMethod,
    formRef,
    isLoading,
    selectedPaymentMethod,
    isPayButtonDisabled,
    handleSubmit,
    isGetPaymentSessionLoading,
    isSubmitting,
  } = usePaymentPurse({
    invoice,
    claimGroupId,
    onSubmitSuccess,
    kindId,
    kind,
  });

  const { clientSessionId } = useGetPurseRedirectionData();

  if (clientSessionId && isGetPaymentSessionLoading) {
    return (
      <ExternalPortal>
        <FullScreenLoader
          isOpen
          isIntlInitialized
          message={safeFormattedIntlString(messages.transactionInProgress)}
        />
      </ExternalPortal>
    );
  }

  return (
    <form onSubmit={handleSubmit} id="purseForm">
      {paymentMethods.length > 1 &&
        paymentMethods.map((paymentMethod, idx) => (
          <FormGroup
            key={`${paymentMethod.partner}-${paymentMethod.method}-${idx}`}
          >
            <ClickableCard
              onClick={() => selectPaymentMethod(paymentMethod)}
              isFluid
              isActive={selectedPaymentMethod?.id === paymentMethod.id}
              shouldDisplayHoverStatus
            >
              <$SelectPayment>
                <PaymentMethodImage paymentMethod={paymentMethod.method} />
                <div>
                  <Bold>
                    <SafeFormattedMessageWithId
                      id={`view.paymentPurse.${paymentMethod.method}`}
                    />
                  </Bold>

                  {getPaymentMethodDescriptionMessage(paymentMethod.method) && (
                    <div>
                      <SafeFormattedMessageWithoutSpread
                        message={
                          getPaymentMethodDescriptionMessage(
                            paymentMethod.method,
                          ) ?? paymentMethod.method
                        }
                      />
                    </div>
                  )}
                </div>
              </$SelectPayment>
            </ClickableCard>
          </FormGroup>
        ))}

      <div ref={formRef} />

      {paymentMethods.length === 0 && <BaseLoader />}

      {paymentMethods.length > 0 && (
        <$PaymentButtonContainer>
          <Button
            id="pay-button"
            fluid
            primary
            disabled={
              isLoading ||
              !selectedPrimaryToken ||
              isPayButtonDisabled ||
              !isFormCompleted
            }
            type={BUTTON_TYPES.SUBMIT}
            icon={<ClosedLockUnfilled />}
            dataTestId="paymentFormSubmitButton"
            isLoading={isGetPaymentSessionLoading || isSubmitting}
          >
            <SafeFormattedMessageWithoutSpread
              message={messages.pay}
              values={{
                amount: formatStripeAmount(
                  invoice.priceWithTax,
                  invoice.currencyTrigram ?? CURRENCIES.EUR,
                ),
              }}
            />
          </Button>
        </$PaymentButtonContainer>
      )}
    </form>
  );
};
